import React from 'react'
import Typography from '@mui/material/Typography'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'

const useStyles = makeStyles()((theme: Theme) => ({
  h1: {
    fontWeight: 'bold',
  },
  h2: {
    fontSize: theme.spacing(2.5),
    fontWeight: 'bold',
  },
  caption: {
    color: theme.palette.text.secondary,
    fontSize: theme.spacing(1.75),
    lineHeight: '160%',
    letterSpacing: 0.5,
  },
  error: {
    fontWeight: 'bold',
    color: theme.palette.secondary.main,
  },
  text: {
    wordBreak: 'break-all',
  },
}))

export const H1: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ children, className }) => {
  const { classes, cx } = useStyles()
  return (
    <Typography
      color="textPrimary"
      variant="h5"
      component="h1"
      className={cx(classes.h1, className)}
    >
      {children}
    </Typography>
  )
}

export const H2: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ children, className }) => {
  const { classes, cx } = useStyles()
  return (
    <Typography
      color="textPrimary"
      variant="h6"
      component="h2"
      className={cx(classes.h2, className)}
    >
      {children}
    </Typography>
  )
}

export const H3: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ children, className }) => (
  <Typography
    color="textPrimary"
    variant="h6"
    component="h3"
    className={className}
  >
    {children}
  </Typography>
)

export const Text1: React.FC<{
  className?: string
  children?: React.ReactNode
}> = ({ children, className }) => {
  const { classes, cx } = useStyles()
  return (
    <Typography
      color="textPrimary"
      variant="body1"
      component="span"
      className={cx(classes.text, className)}
      align="inherit"
    >
      {children}
    </Typography>
  )
}

export const Text2: React.FC<{
  className?: string
  gray?: boolean
  children?: React.ReactNode
}> = ({ children, gray, className }) => {
  const { classes, cx } = useStyles()
  return (
    <Typography
      color={gray ? 'textPrimary' : 'textSecondary'}
      variant="body2"
      component="span"
      className={cx(classes.text, className)}
      align="inherit"
    >
      {children}
    </Typography>
  )
}

export const Caption: React.FC<{
  className?: string
  error?: boolean
  children?: React.ReactNode
}> = ({ children, error, className }) => {
  const { classes, cx } = useStyles()

  return (
    <small className={cx(classes.caption, error && classes.error, className)}>
      {children}
    </small>
  )
}
